import { formatIsoDateBR } from "~/utils/format";

export const MERGEABLE_STATUSES = [
  "waiting_supplier_company_input_vacancies_data",
  "waiting_generate_doctor_contract",
];

export const STATUS_SHORT_LABEL = {
  waiting_supplier_company_input_vacancies_data: "Com a empresa",
  waiting_generate_doctor_contract: "Aguardando contrato",
  waiting_doctor_sign_contract: "Aguardando assinatura",
  waiting_payment_doctor: "Aguardando pagamento",
  merged: "Agrupado",
};

export const BLOCK_REASON_TEXT = {
  CONTRACT_GENERATED: "🔒 contrato gerado: não junta",
  STATUS_NOT_MERGEABLE: "⛔ status não permite juntar",
  ALREADY_MERGED: "encerrado, consultável",
  EMPTY_PACKAGE: "⛔ pacote sem plantões",
};

const normalizeDate = (value) => (value ? String(value).slice(0, 10) : null);

export const buildSections = (groups) => {
  const map = new Map();
  groups.forEach((group) => {
    const key = `${group.doctor.id}|${group.company.id}`;
    if (!map.has(key)) {
      map.set(key, { key, doctor: group.doctor, company: group.company, rows: [], suggestion: null, joinableCount: 0 });
    }
    const section = map.get(key);
    const live = group.packages.filter((p) => !p.mergedIntoAdvancePaymentId);
    group.packages.forEach((pkg) => {
      // AB D17: status/data vêm do pacote; o grupo é só fallback para AB antigo.
      const own = pkg.status || group.status;
      section.rows.push({
        ...pkg,
        status: own === "merged" || pkg.mergedIntoAdvancePaymentId ? "merged" : own,
        paymentDate: normalizeDate(pkg.paymentDate !== undefined ? pkg.paymentDate : group.paymentDate),
        contractGenerated: pkg.contractGenerated === true,
        groupKey: group.key,
        mergeable: group.mergeable,
        blockReason: group.blockReason || null,
        doctor: group.doctor,
        company: group.company,
      });
    });
    const isMergeableStatus = MERGEABLE_STATUSES.includes(group.status);
    const joinable = live.filter((p) => p.contractGenerated !== true);
    if (isMergeableStatus) section.joinableCount += joinable.length;
    if (group.mergeable && isMergeableStatus && joinable.length >= 2 &&
        (!section.suggestion || joinable.length > section.suggestion.ids.length)) {
      section.suggestion = { ids: joinable.map((p) => p.id).sort((a, b) => a - b) };
    }
  });
  const list = [...map.values()];
  list.forEach((s) => s.rows.sort((a, b) => (a.status === "merged") - (b.status === "merged") || a.id - b.id));
  return list
    .map((section, index) => ({ section, index }))
    .sort((a, b) => (b.section.suggestion ? 1 : 0) - (a.section.suggestion ? 1 : 0) || a.index - b.index)
    .map(({ section }) => section);
};

export const indexRows = (sections) => {
  const map = new Map();
  sections.forEach((s) => s.rows.forEach((r) => map.set(r.id, r)));
  return map;
};

export const getAnchor = (selectedIds, rowsById) =>
  selectedIds.length ? rowsById.get(selectedIds[0]) || null : null;

export const getRowLock = (row, anchor) => {
  if (row.status === "merged") return { kind: "merged", text: BLOCK_REASON_TEXT.ALREADY_MERGED };
  if (row.contractGenerated) return { kind: "blocked", text: BLOCK_REASON_TEXT.CONTRACT_GENERATED };
  // MIN_TWO_PACKAGES só diz que o grupo sozinho não tem par; a linha segue selecionável para a trava explicar o motivo.
  if (row.blockReason && row.blockReason !== "MIN_TWO_PACKAGES") {
    return { kind: "blocked", text: BLOCK_REASON_TEXT[row.blockReason] || "⛔ não pode ser juntado" };
  }
  if (!MERGEABLE_STATUSES.includes(row.status)) return { kind: "blocked", text: BLOCK_REASON_TEXT.STATUS_NOT_MERGEABLE };
  if (!anchor || anchor.id === row.id) return null;
  if (row.company.id !== anchor.company.id) return { kind: "off", text: `⛔ outra empresa (${row.company.name})` };
  if (row.doctor.id !== anchor.doctor.id) return { kind: "off", text: "⛔ outro médico" };
  if (row.status !== anchor.status) {
    return { kind: "off", text: `⛔ status diferente (${STATUS_SHORT_LABEL[row.status] || row.status})` };
  }
  if ((row.paymentDate || null) !== (anchor.paymentDate || null)) {
    if (row.paymentDate && anchor.paymentDate) {
      return { kind: "off", text: `⛔ data de pagamento do pacote diferente (${formatIsoDateBR(row.paymentDate)})` };
    }
    if (row.paymentDate) return { kind: "off", text: `⛔ pacote já tem data de pagamento (${formatIsoDateBR(row.paymentDate)})` };
    return { kind: "off", text: "⛔ pacote sem data de pagamento" };
  }
  return null;
};

export const canJoin = (selectedIds, rowsById) => {
  if (selectedIds.length < 2) return false;
  const anchor = getAnchor(selectedIds, rowsById);
  if (!anchor) return false;
  return selectedIds.every((id) => {
    const row = rowsById.get(id);
    return Boolean(row) && getRowLock(row, anchor) === null;
  });
};

export const getLockSummary = (anchor) =>
  `🔒 Seleção travada em ${anchor.company.name} · ${anchor.doctor.name} · ${STATUS_SHORT_LABEL[anchor.status] || anchor.status} · pagamento do pacote ${
    anchor.paymentDate ? formatIsoDateBR(anchor.paymentDate) : "a definir"
  }. Pacotes de outra empresa (ou outro médico, status ou data) ficam desabilitados, com o motivo na linha.`;
